// App.js
// PLANURA RESOLVE DASHBOARD - VERSÃO COMPLETA COM UPLOAD E USUÁRIOS
// Sistema de permissões: ADM (acesso total) e Usuário Simples (sem relatórios)

import React, { useState, useMemo, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { Calendar, Users, FileText, Activity, TrendingUp, LogOut, Eye, Bell, Download, File, Save, X, Search, Filter, RefreshCw, ChevronDown, Newspaper, CalendarDays, Upload, Trash2, Edit, Star, UserPlus, Image as ImageIcon, Paperclip } from 'lucide-react';

// IMPORTAÇÕES DO FIREBASE
import { db, storage } from './firebase-config';
import { 
  collection, addDoc, getDocs, doc, updateDoc, deleteDoc, getDoc,
  query, orderBy, where, Timestamp 
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

const Dashboard = () => {
  // ESTADOS
  const [currentUser, setCurrentUser] = useState(null);
  const [loginInput, setLoginInput] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [formData, setFormData] = useState({});
  const [selectedItem, setSelectedItem] = useState(null);
  const [demandas, setDemandas] = useState([]);
  const [consultas, setConsultas] = useState([]);
  const [noticias, setNoticias] = useState([]);
  const [agenda, setAgenda] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  
  // ARQUIVOS PARA UPLOAD
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  
  // FILTRO DE PERÍODO PARA GRÁFICOS
  const [periodoGraficos, setPeriodoGraficos] = useState('30dias');
  const [dataInicioGraficos, setDataInicioGraficos] = useState('');
  const [dataFimGraficos, setDataFimGraficos] = useState('');
  
  const [filtros, setFiltros] = useState({
    busca: '', periodo: '30dias', dataInicio: '', dataFim: '',
    categoriaDemanda: 'todas', estadoDemanda: 'todos', avaliacaoMin: 0,
    tipoConsulta: 'todos', statusConsulta: 'todos', precisaTFD: 'todos', temEmail: 'todos',
    publicadoNoticia: 'todos', publicadoEvento: 'todos'
  });

  // VERIFICAR PERMISSÕES
  const isMasterAdmin = currentUser?.role === 'master';
  const isAdmin = currentUser?.role === 'admin';
  const isServidor = currentUser?.role === 'servidor';
  const canAccessReports = isMasterAdmin || isAdmin;
  const canManageData = isMasterAdmin || isAdmin || isServidor;
  const canManageUsers = isMasterAdmin;

  // CARREGAR DADOS
  const carregarDados = async (tipo, setter) => {
    try {
      setLoading(true);
      const ref = collection(db, tipo);
      const orderField = tipo === 'agenda' ? 'data' : tipo === 'noticias' ? 'created_at' : tipo === 'demandas' ? 'created_at' : 'created_time';
      const q = query(ref, orderBy(orderField, 'desc'));
      const snapshot = await getDocs(q);
      const dados = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        [orderField]: doc.data()[orderField]?.toDate?.()?.toISOString().split('T')[0] || doc.data()[orderField] || new Date().toISOString().split('T')[0]
      }));
      setter(dados);
    } catch (error) {
      console.error(`Erro ao carregar ${tipo}:`, error);
      alert(`Erro ao carregar ${tipo}: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const carregarUsuarios = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'usuarios'));
      const users = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setUsuarios(users);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    }
  };

  useEffect(() => {
    if (currentUser) {
      carregarDados('demandas', setDemandas);
      carregarDados('consultas', setConsultas);
      carregarDados('noticias', setNoticias);
      carregarDados('agenda', setAgenda);
      if (canManageUsers) carregarUsuarios();
    }
  }, [currentUser, canManageUsers]);

  // UPLOAD DE IMAGEM
  const uploadImagem = async (file, pasta) => {
    if (!file) return null;
    try {
      const timestamp = Date.now();
      const fileName = `${pasta}/${timestamp}_${file.name}`;
      const storageRef = ref(storage, fileName);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      return url;
    } catch (error) {
      console.error('Erro no upload:', error);
      alert('Erro ao fazer upload da imagem: ' + error.message);
      return null;
    }
  };

  // UPLOAD DE DOCUMENTOS
  const uploadDocumentos = async (files) => {
    if (!files || files.length === 0) return [];
    try {
      const urls = [];
      for (let file of files) {
        const timestamp = Date.now();
        const fileName = `documentos/${timestamp}_${file.name}`;
        const storageRef = ref(storage, fileName);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        urls.push({ url, nome: file.name, timestamp });
      }
      return urls;
    } catch (error) {
      console.error('Erro no upload de documentos:', error);
      alert('Erro ao fazer upload dos documentos: ' + error.message);
      return [];
    }
  };

  // CRUD GENÉRICO
  const salvar = async (tipo, dados, isNew) => {
    try {
      setLoading(true);
      
      // Upload de imagem se houver
      if (selectedImage && (tipo === 'noticias' || tipo === 'agenda' || tipo === 'demandas')) {
        const pasta = tipo === 'noticias' ? 'noticias' : tipo === 'agenda' ? 'eventos' : 'demandas';
        const imageUrl = await uploadImagem(selectedImage, pasta);
        if (imageUrl) {
          dados.imagem = imageUrl;
        }
      }

      // Upload de documentos se houver
      if (selectedDocuments.length > 0 && tipo === 'consultas') {
        const docsUrls = await uploadDocumentos(selectedDocuments);
        dados.documentos = [...(selectedItem?.documentos || []), ...docsUrls];
      }

      if (isNew) {
        await addDoc(collection(db, tipo), { 
          ...dados, 
          created_at: Timestamp.now(), 
          autor: currentUser.name 
        });
        alert('✅ Criado com sucesso!');
      } else {
        await updateDoc(doc(db, tipo, selectedItem.id), dados);
        alert('✅ Atualizado com sucesso!');
      }
      
      if (tipo === 'noticias') carregarDados('noticias', setNoticias);
      else if (tipo === 'agenda') carregarDados('agenda', setAgenda);
      else if (tipo === 'demandas') carregarDados('demandas', setDemandas);
      else if (tipo === 'consultas') carregarDados('consultas', setConsultas);
      else if (tipo === 'usuarios') carregarUsuarios();
      
      setShowModal(false);
      setSelectedImage(null);
      setSelectedDocuments([]);
    } catch (error) {
      alert('❌ Erro: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const deletar = async (tipo, id) => {
    if (!window.confirm('❓ Tem certeza?')) return;
    try {
      setLoading(true);
      await deleteDoc(doc(db, tipo, id));
      alert('✅ Deletado!');
      if (tipo === 'noticias') carregarDados('noticias', setNoticias);
      else if (tipo === 'agenda') carregarDados('agenda', setAgenda);
      else if (tipo === 'demandas') carregarDados('demandas', setDemandas);
      else if (tipo === 'consultas') carregarDados('consultas', setConsultas);
      else if (tipo === 'usuarios') carregarUsuarios();
    } catch (error) {
      alert('❌ Erro: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const deletarDocumento = async (consulta, docIndex) => {
    if (!window.confirm('❓ Deletar este documento?')) return;
    try {
      const novosDocumentos = consulta.documentos.filter((_, idx) => idx !== docIndex);
      await updateDoc(doc(db, 'consultas', consulta.id), { documentos: novosDocumentos });
      carregarDados('consultas', setConsultas);
      alert('✅ Documento deletado!');
    } catch (error) {
      alert('❌ Erro: ' + error.message);
    }
  };

  // FILTROS
  const calcularPeriodo = (periodo) => {
    const hoje = new Date();
    let dataInicio = new Date();
    switch(periodo) {
      case '7dias': dataInicio.setDate(hoje.getDate() - 7); break;
      case '30dias': dataInicio.setDate(hoje.getDate() - 30); break;
      case '90dias': dataInicio.setDate(hoje.getDate() - 90); break;
      case 'ano': dataInicio.setFullYear(hoje.getFullYear() - 1); break;
      case 'todos': dataInicio = new Date('2000-01-01'); break;
      default: dataInicio.setDate(hoje.getDate() - 30);
    }
    return { inicio: dataInicio, fim: hoje };
  };

  const aplicarFiltros = (dados, campos, dateField = 'created_at') => {
    let resultado = [...dados];
    
    if (filtros.dataInicio && filtros.dataFim) {
      resultado = resultado.filter(item => {
        const data = new Date(item[dateField]);
        return data >= new Date(filtros.dataInicio) && data <= new Date(filtros.dataFim);
      });
    } else if (filtros.periodo) {
      const { inicio, fim } = calcularPeriodo(filtros.periodo);
      resultado = resultado.filter(item => {
        const data = new Date(item[dateField]);
        return data >= inicio && data <= fim;
      });
    }
    
    if (filtros.busca) {
      const busca = filtros.busca.toLowerCase();
      resultado = resultado.filter(item => 
        campos.some(campo => item[campo]?.toLowerCase().includes(busca))
      );
    }
    
    return resultado;
  };

  const filtrarPorPeriodoGraficos = (dados, dateField = 'created_at') => {
    if (dataInicioGraficos && dataFimGraficos) {
      return dados.filter(item => {
        const data = new Date(item[dateField]);
        return data >= new Date(dataInicioGraficos) && data <= new Date(dataFimGraficos);
      });
    } else if (periodoGraficos) {
      const { inicio, fim } = calcularPeriodo(periodoGraficos);
      return dados.filter(item => {
        const data = new Date(item[dateField]);
        return data >= inicio && data <= fim;
      });
    }
    return dados;
  };

  const demandasFiltradas = useMemo(() => {
    let resultado = aplicarFiltros(demandas, ['Nome', 'descricao', 'endereco', 'categoria'], 'created_at');
    if (filtros.categoriaDemanda !== 'todas') resultado = resultado.filter(d => d.categoria === filtros.categoriaDemanda);
    if (filtros.estadoDemanda !== 'todos') resultado = resultado.filter(d => d.estado === filtros.estadoDemanda);
    if (filtros.avaliacaoMin > 0) resultado = resultado.filter(d => d.rating && d.rating >= filtros.avaliacaoMin);
    return resultado;
  }, [demandas, filtros]);

  const consultasFiltradas = useMemo(() => {
    let resultado = aplicarFiltros(consultas, ['NomeP', 'email', 'CelP', 'TipoDeConsulta'], 'created_time');
    if (filtros.tipoConsulta !== 'todos') resultado = resultado.filter(c => c.TipoDeConsulta === filtros.tipoConsulta);
    if (filtros.statusConsulta !== 'todos') resultado = resultado.filter(c => c.statusConsulta === filtros.statusConsulta);
    if (filtros.precisaTFD !== 'todos') resultado = resultado.filter(c => c.precisaTransporte === (filtros.precisaTFD === 'sim'));
    if (filtros.temEmail !== 'todos') {
      if (filtros.temEmail === 'sim') resultado = resultado.filter(c => c.email && c.email.length > 0);
      else resultado = resultado.filter(c => !c.email || c.email.length === 0);
    }
    return resultado;
  }, [consultas, filtros]);

  const noticiasFiltradas = useMemo(() => {
    let resultado = aplicarFiltros(noticias, ['titulo', 'conteudo'], 'created_at');
    if (filtros.publicadoNoticia !== 'todos') resultado = resultado.filter(n => n.publicado === (filtros.publicadoNoticia === 'sim'));
    return resultado.slice(0, 4); // Últimas 4
  }, [noticias, filtros]);

  const agendaFiltrada = useMemo(() => {
    let resultado = aplicarFiltros(agenda, ['titulo', 'local'], 'data');
    if (filtros.publicadoEvento !== 'todos') resultado = resultado.filter(e => e.publicado === (filtros.publicadoEvento === 'sim'));
    return resultado.slice(0, 4); // Últimos 4
  }, [agenda, filtros]);

  // CÁLCULOS PARA GRÁFICOS
  const dadosGraficos = useMemo(() => {
    const demandasPeriodo = filtrarPorPeriodoGraficos(demandas, 'created_at');
    const consultasPeriodo = filtrarPorPeriodoGraficos(consultas, 'created_time');

    const statusCount = { 'Pendente': 0, 'Em Análise': 0, 'Encaminhado': 0, 'Completo': 0, 'Cancelado': 0, 'Negado': 0 };
    demandasPeriodo.forEach(d => { const status = d.estado || 'Pendente'; if (statusCount[status] !== undefined) statusCount[status]++; });
    const graficoStatus = Object.keys(statusCount).map(status => ({ name: status, value: statusCount[status] }));

    const tabelaCruzada = {};
    demandasPeriodo.forEach(d => {
      const status = d.estado || 'Pendente';
      const setor = d.setorEncaminhado || 'Sem Setor';
      if (!tabelaCruzada[setor]) tabelaCruzada[setor] = { 'Pendente': 0, 'Em Análise': 0, 'Encaminhado': 0, 'Completo': 0, 'Cancelado': 0, 'Negado': 0, 'Total': 0 };
      if (tabelaCruzada[setor][status] !== undefined) tabelaCruzada[setor][status]++;
      tabelaCruzada[setor]['Total']++;
    });

    const totalConsultas = consultasPeriodo.length;
    const totalTFD = consultasPeriodo.filter(c => c.precisaTransporte === true).length;

    const avaliacoes = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    let somaAvaliacoes = 0, totalAvaliacoes = 0;
    demandasPeriodo.forEach(d => { if (d.rating && d.rating >= 1 && d.rating <= 5) { avaliacoes[d.rating]++; somaAvaliacoes += d.rating; totalAvaliacoes++; } });
    const graficoAvaliacoes = [{ name: '1★', value: avaliacoes[1] }, { name: '2★', value: avaliacoes[2] }, { name: '3★', value: avaliacoes[3] }, { name: '4★', value: avaliacoes[4] }, { name: '5★', value: avaliacoes[5] }];
    const mediaAvaliacoes = totalAvaliacoes > 0 ? (somaAvaliacoes / totalAvaliacoes).toFixed(2) : 0;

    return { graficoStatus, tabelaCruzada, totalConsultas, totalTFD, graficoAvaliacoes, mediaAvaliacoes, totalAvaliacoes };
  }, [demandas, consultas, periodoGraficos, dataInicioGraficos, dataFimGraficos]);

  const stats = useMemo(() => {
    const totalDemandas = demandasFiltradas.length;
    const demandasCompletas = demandasFiltradas.filter(d => d.estado === 'Completo').length;
    const ratings = demandasFiltradas.filter(d => d.rating).map(d => d.rating);
    return {
      totalDemandas, demandasCompletas,
      totalConsultas: consultasFiltradas.length,
      totalNoticias: noticias.length, noticiasPublicadas: noticias.filter(n => n.publicado).length,
      totalEventos: agenda.length, eventosPublicados: agenda.filter(e => e.publicado).length,
      avgRating: ratings.length > 0 ? (ratings.reduce((a,b) => a+b) / ratings.length).toFixed(1) : 0,
      taxaConclusao: totalDemandas > 0 ? ((demandasCompletas / totalDemandas) * 100).toFixed(1) : 0
    };
  }, [demandasFiltradas, consultasFiltradas, noticias, agenda]);

  // LOGIN
  const handleLogin = async () => {
    const input = loginInput.trim();
    
    // ADM Master
    if (input === '57409200600' || input === 'admin') {
      setCurrentUser({ role: 'master', name: 'Master Admin', cpf: '57409200600' });
      setActiveTab('dashboard');
      return;
    }
    
    // Buscar usuário pelo CPF
    try {
      const q = query(collection(db, 'usuarios'), where('cpf', '==', input));
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        const userData = snapshot.docs[0].data();
        setCurrentUser({ 
          role: userData.tipo || 'servidor',
          name: userData.nome, 
          cpf: userData.cpf,
          id: snapshot.docs[0].id 
        });
        // Define aba inicial baseado no tipo
        if (userData.tipo === 'admin') {
          setActiveTab('dashboard');
        } else {
          setActiveTab('demandas');
        }
      } else {
        alert('❌ CPF não encontrado. Contate o administrador.');
      }
    } catch (error) {
      alert('❌ Erro ao fazer login: ' + error.message);
    }
  };

  const openModal = (type, item = null) => {
    setModalType(type);
    setSelectedItem(item);
    setSelectedImage(null);
    setSelectedDocuments([]);
    
    if (type === 'newNoticia' || type === 'editNoticia') {
      setFormData({ titulo: item?.titulo || '', link: item?.link || '', conteudo: item?.conteudo || '', publicado: item?.publicado || false, imagem: item?.imagem || '' });
    } else if (type === 'newEvento' || type === 'editEvento') {
      setFormData({ titulo: item?.titulo || '', data: item?.data || '', local: item?.local || '', link: item?.link || '', publicado: item?.publicado || false, imagem: item?.imagem || '' });
    } else if (type === 'newDemanda' || type === 'editDemanda') {
      setFormData({ categoria: item?.categoria || '', descricao: item?.descricao || '', endereco: item?.endereco || '', estado: item?.estado || 'Pendente', setorEncaminhado: item?.setorEncaminhado || '', emailSetor: item?.emailSetor || '', imagem: item?.imagem || '' });
    } else if (type === 'newConsulta' || type === 'editConsulta') {
      setFormData({ 
        NomeP: item?.NomeP || '', 
        email: item?.email || '', 
        CelP: item?.CelP || '',
        TipoDeConsulta: item?.TipoDeConsulta || '', 
        statusConsulta: item?.statusConsulta || 'Agendado', 
        horaP: item?.horaP || '', 
        enderecoConsulta: item?.enderecoConsulta || '', 
        precisaTransporte: item?.precisaTransporte || false,
        enderecoColeta: item?.enderecoColeta || '',
        documentos: item?.documentos || []
      });
    } else if (type === 'newUsuario' || type === 'editUsuario') {
      setFormData({ nome: item?.nome || '', cpf: item?.cpf || '', tipo: item?.tipo || 'servidor' });
    } else if (type === 'viewConsulta') {
      // Modo visualização
      setFormData(item);
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    if (modalType.includes('Noticia')) await salvar('noticias', formData, modalType === 'newNoticia');
    else if (modalType.includes('Evento')) await salvar('agenda', formData, modalType === 'newEvento');
    else if (modalType.includes('Demanda')) await salvar('demandas', { ...formData, created_at: Timestamp.now(), Nome: currentUser.name, rating: null }, modalType === 'newDemanda');
    else if (modalType.includes('Consulta')) await salvar('consultas', formData, modalType === 'newConsulta');
    else if (modalType.includes('Usuario')) await salvar('usuarios', formData, modalType === 'newUsuario');
  };

  // LOGIN SCREEN
  if (!currentUser) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 to-blue-800 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-2">
            {/* LADO ESQUERDO - LOGO AI TECNO */}
            <div className="hidden md:flex bg-gradient-to-br from-gray-50 to-gray-100 p-12 items-center justify-center">
              <div className="text-center">
                <img src="/logo-aitecno.png" alt="AI Tecno Gestão Pública" className="w-full max-w-sm mb-6 mx-auto" />
                <p className="text-gray-600 text-lg font-medium">Sistema de Gestão Municipal</p>
                <p className="text-gray-500 text-sm mt-2">Tecnologia e Inovação</p>
              </div>
            </div>

            {/* LADO DIREITO - FORMULÁRIO */}
            <div className="p-8">
              <div className="text-center mb-8">
                {/* Logo Mobile */}
                <div className="md:hidden mb-4">
                  <img src="/logo-prefeitura.png" alt="Prefeitura Resolve" className="w-64 mx-auto" />
                </div>
                <div className="inline-block bg-blue-600 text-white p-4 rounded-full mb-4"><Users size={40} /></div>
                <h1 className="text-2xl font-bold text-gray-800">Acesso ao Sistema</h1>
                <p className="text-gray-600 mt-2">Entre com seu CPF</p>
              </div>
              <input type="text" value={loginInput} onChange={(e) => setLoginInput(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleLogin()} className="w-full px-4 py-3 border rounded-lg mb-4" placeholder="CPF (apenas números)" />
              <button onClick={handleLogin} className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700">Entrar</button>
              <div className="mt-6 p-4 bg-blue-50 rounded-lg text-xs">
                <p className="font-semibold mb-2">Acesso Master:</p>
                <p>CPF: 57409200600</p>
                <p className="mt-2 text-gray-600">Usuários cadastrados: entre com CPF</p>
              </div>
              
              {/* RODAPÉ */}
              <div className="mt-8 pt-6 border-t text-center">
                <p className="text-xs text-gray-400">Desenvolvido por AI Tecno Gestão Pública LTDA</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // PAINEL FILTROS
  const PainelFiltros = () => (
    <div className="bg-white rounded-xl shadow-md p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2"><Filter size={20} className="text-blue-600" /><h3 className="text-lg font-bold">Filtros</h3></div>
        <div className="flex gap-2">
          <button onClick={() => setFiltros({ busca: '', periodo: '30dias', dataInicio: '', dataFim: '', categoriaDemanda: 'todas', estadoDemanda: 'todos', avaliacaoMin: 0, tipoConsulta: 'todos', statusConsulta: 'todos', precisaTFD: 'todos', temEmail: 'todos', publicadoNoticia: 'todos', publicadoEvento: 'todos' })} className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"><RefreshCw size={16} />Limpar</button>
          <button onClick={() => setMostrarFiltros(!mostrarFiltros)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg"><ChevronDown size={16} className={mostrarFiltros ? 'rotate-180' : ''} />{mostrarFiltros ? 'Ocultar' : 'Mostrar'}</button>
        </div>
      </div>
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
        <input type="text" value={filtros.busca} onChange={(e) => setFiltros({...filtros, busca: e.target.value})} placeholder="🔍 Buscar..." className="w-full pl-10 pr-4 py-3 border rounded-lg" />
      </div>
      {mostrarFiltros && (
        <div className="space-y-4 pt-4 border-t">
          <div className="grid grid-cols-3 gap-4">
            <select value={filtros.periodo} onChange={(e) => setFiltros({...filtros, periodo: e.target.value, dataInicio: '', dataFim: ''})} className="px-4 py-2 border rounded-lg"><option value="7dias">7 dias</option><option value="30dias">30 dias</option><option value="90dias">90 dias</option><option value="ano">Ano</option><option value="todos">Todos</option></select>
            <input type="date" value={filtros.dataInicio} onChange={(e) => setFiltros({...filtros, dataInicio: e.target.value, periodo: ''})} className="px-4 py-2 border rounded-lg" />
            <input type="date" value={filtros.dataFim} onChange={(e) => setFiltros({...filtros, dataFim: e.target.value, periodo: ''})} className="px-4 py-2 border rounded-lg" />
          </div>
        </div>
      )}
    </div>
  );

  const CORES_STATUS = { 'Pendente': '#EF4444', 'Em Análise': '#F59E0B', 'Encaminhado': '#3B82F6', 'Completo': '#10B981', 'Cancelado': '#6B7280', 'Negado': '#DC2626' };
  const CORES_AVALIACOES = ['#EF4444', '#F59E0B', '#FCD34D', '#84CC16', '#10B981'];

  // MAIN RENDER
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-gradient-to-r from-blue-900 to-blue-800 text-white shadow-lg">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <img src="/logo-prefeitura.png" alt="Prefeitura Resolve" className="h-12 object-contain" />
            <div>
              <p className="text-blue-200 text-sm">
                {currentUser.name} 
                {isMasterAdmin && ' (Master)'}
                {isAdmin && ' (Administrador)'}
                {isServidor && ' (Servidor)'}
              </p>
            </div>
          </div>
          <button onClick={() => setCurrentUser(null)} className="flex items-center gap-2 bg-blue-700 px-4 py-2 rounded-lg"><LogOut size={18} />Sair</button>
        </div>
      </header>

      {loading && <div className="fixed top-0 left-0 right-0 bg-blue-600 text-white text-center py-2 z-50">⏳ Carregando...</div>}

      <div className="container mx-auto px-6 py-8">
        <div className="bg-white rounded-xl shadow-md mb-8">
          <div className="flex gap-2 p-2 overflow-x-auto">
            {canAccessReports && <button onClick={() => setActiveTab('dashboard')} className={`px-6 py-3 rounded-lg font-semibold whitespace-nowrap ${activeTab === 'dashboard' ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}>📊 Dashboard</button>}
            {canManageData && <button onClick={() => setActiveTab('demandas')} className={`px-6 py-3 rounded-lg font-semibold whitespace-nowrap ${activeTab === 'demandas' ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}>📋 Demandas ({demandasFiltradas.length})</button>}
            {canManageData && <button onClick={() => setActiveTab('consultas')} className={`px-6 py-3 rounded-lg font-semibold whitespace-nowrap ${activeTab === 'consultas' ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}>🏥 Exames ({consultasFiltradas.length})</button>}
            {canManageData && <button onClick={() => setActiveTab('noticias')} className={`px-6 py-3 rounded-lg font-semibold whitespace-nowrap ${activeTab === 'noticias' ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}>📰 Notícias ({noticiasFiltradas.length})</button>}
            {canManageData && <button onClick={() => setActiveTab('agenda')} className={`px-6 py-3 rounded-lg font-semibold whitespace-nowrap ${activeTab === 'agenda' ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}>📅 Agenda ({agendaFiltrada.length})</button>}
            {canManageUsers && <button onClick={() => setActiveTab('usuarios')} className={`px-6 py-3 rounded-lg font-semibold whitespace-nowrap ${activeTab === 'usuarios' ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}>👥 Usuários ({usuarios.length})</button>}
          </div>
        </div>

        {activeTab !== 'dashboard' && activeTab !== 'usuarios' && <PainelFiltros />}

        {/* DASHBOARD COM GRÁFICOS */}
        {activeTab === 'dashboard' && canAccessReports && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2"><Calendar size={20} className="text-blue-600" />Período dos Gráficos</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div><label className="block text-sm font-medium mb-2">Período Rápido</label><select value={periodoGraficos} onChange={(e) => { setPeriodoGraficos(e.target.value); setDataInicioGraficos(''); setDataFimGraficos(''); }} className="w-full px-4 py-2 border rounded-lg"><option value="7dias">Últimos 7 dias</option><option value="30dias">Últimos 30 dias</option><option value="90dias">Últimos 90 dias</option><option value="ano">Último ano</option><option value="todos">Todos os períodos</option></select></div>
                <div><label className="block text-sm font-medium mb-2">Data Início</label><input type="date" value={dataInicioGraficos} onChange={(e) => { setDataInicioGraficos(e.target.value); setPeriodoGraficos(''); }} className="w-full px-4 py-2 border rounded-lg" /></div>
                <div><label className="block text-sm font-medium mb-2">Data Fim</label><input type="date" value={dataFimGraficos} onChange={(e) => { setDataFimGraficos(e.target.value); setPeriodoGraficos(''); }} className="w-full px-4 py-2 border rounded-lg" /></div>
              </div>
            </div>

            <div className="grid grid-cols-4 gap-6">
              {[
                { icon: FileText, color: 'blue', label: 'Demandas', value: stats.totalDemandas },
                { icon: Activity, color: 'green', label: 'Conclusão', value: stats.taxaConclusao + '%' },
                { icon: Calendar, color: 'purple', label: 'Consultas', value: stats.totalConsultas },
                { icon: TrendingUp, color: 'yellow', label: 'Avaliação', value: stats.avgRating }
              ].map((stat, i) => (
                <div key={i} className="bg-white rounded-xl shadow-md p-6">
                  <div className="flex items-center justify-between">
                    <div className={`bg-${stat.color}-100 p-3 rounded-lg`}><stat.icon className={`text-${stat.color}-600`} size={24} /></div>
                    <div className="text-right"><p className="text-sm text-gray-600">{stat.label}</p><p className="text-3xl font-bold">{stat.value}</p></div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-lg font-bold mb-4">📊 Demandas por Status</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={dadosGraficos.graficoStatus}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="value" fill="#3B82F6" name="Quantidade">{dadosGraficos.graficoStatus.map((entry, index) => (<Cell key={`cell-${index}`} fill={CORES_STATUS[entry.name] || '#3B82F6'} />))}</Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-lg font-bold mb-4">📋 Demandas por Setor e Status</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50"><tr><th className="px-4 py-3 text-left font-semibold">Setor</th><th className="px-4 py-3 text-center font-semibold">Pendente</th><th className="px-4 py-3 text-center font-semibold">Em Análise</th><th className="px-4 py-3 text-center font-semibold">Encaminhado</th><th className="px-4 py-3 text-center font-semibold">Completo</th><th className="px-4 py-3 text-center font-semibold">Cancelado</th><th className="px-4 py-3 text-center font-semibold">Negado</th><th className="px-4 py-3 text-center font-semibold bg-blue-100">Total</th></tr></thead>
                  <tbody>
                    {Object.keys(dadosGraficos.tabelaCruzada).map((setor, idx) => (
                      <tr key={idx} className="border-t hover:bg-gray-50"><td className="px-4 py-3 font-medium">{setor}</td><td className="px-4 py-3 text-center">{dadosGraficos.tabelaCruzada[setor]['Pendente']}</td><td className="px-4 py-3 text-center">{dadosGraficos.tabelaCruzada[setor]['Em Análise']}</td><td className="px-4 py-3 text-center">{dadosGraficos.tabelaCruzada[setor]['Encaminhado']}</td><td className="px-4 py-3 text-center">{dadosGraficos.tabelaCruzada[setor]['Completo']}</td><td className="px-4 py-3 text-center">{dadosGraficos.tabelaCruzada[setor]['Cancelado']}</td><td className="px-4 py-3 text-center">{dadosGraficos.tabelaCruzada[setor]['Negado']}</td><td className="px-4 py-3 text-center font-bold bg-blue-50">{dadosGraficos.tabelaCruzada[setor]['Total']}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white rounded-xl shadow-md p-6"><h3 className="text-lg font-bold mb-4">🏥 Consultas</h3><div className="text-center"><div className="text-6xl font-bold text-blue-600">{dadosGraficos.totalConsultas}</div><p className="text-gray-600 mt-2">Total de Consultas</p></div></div>
              <div className="bg-white rounded-xl shadow-md p-6"><h3 className="text-lg font-bold mb-4">🚗 TFD Necessários</h3><div className="text-center"><div className="text-6xl font-bold text-green-600">{dadosGraficos.totalTFD}</div><p className="text-gray-600 mt-2">Tratamento Fora Domicílio</p></div></div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-lg font-bold mb-4">⭐ Avaliações das Demandas</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div><ResponsiveContainer width="100%" height={300}><BarChart data={dadosGraficos.graficoAvaliacoes}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" /><YAxis /><Tooltip /><Legend /><Bar dataKey="value" fill="#10B981" name="Quantidade">{dadosGraficos.graficoAvaliacoes.map((entry, index) => (<Cell key={`cell-${index}`} fill={CORES_AVALIACOES[index]} />))}</Bar></BarChart></ResponsiveContainer></div>
                <div className="flex flex-col justify-center items-center">
                  <div className="text-center mb-6"><div className="text-6xl font-bold text-yellow-500 mb-2">{dadosGraficos.mediaAvaliacoes}</div><div className="flex justify-center gap-1 mb-2">{[1,2,3,4,5].map(i => (<Star key={i} size={24} className={i <= Math.round(dadosGraficos.mediaAvaliacoes) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'} />))}</div><p className="text-gray-600">Média Geral Ponderada</p><p className="text-sm text-gray-500 mt-2">Baseado em {dadosGraficos.totalAvaliacoes} avaliações</p></div>
                  <div className="w-full space-y-2">{[...dadosGraficos.graficoAvaliacoes].reverse().map((av, idx) => (<div key={idx} className="flex items-center gap-2"><span className="text-sm font-medium w-8">{av.name}</span><div className="flex-1 bg-gray-200 rounded-full h-4"><div className="bg-yellow-400 h-4 rounded-full" style={{ width: `${dadosGraficos.totalAvaliacoes > 0 ? (av.value / dadosGraficos.totalAvaliacoes * 100) : 0}%` }}></div></div><span className="text-sm text-gray-600 w-12 text-right">{av.value}</span></div>))}</div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white rounded-xl shadow-md p-6"><h3 className="font-bold mb-4">📰 Notícias</h3><div className="space-y-2"><div className="flex justify-between"><span>Total:</span><strong>{stats.totalNoticias}</strong></div><div className="flex justify-between"><span>Publicadas:</span><strong className="text-green-600">{stats.noticiasPublicadas}</strong></div></div></div>
              <div className="bg-white rounded-xl shadow-md p-6"><h3 className="font-bold mb-4">📅 Eventos</h3><div className="space-y-2"><div className="flex justify-between"><span>Total:</span><strong>{stats.totalEventos}</strong></div><div className="flex justify-between"><span>Publicados:</span><strong className="text-green-600">{stats.eventosPublicados}</strong></div></div></div>
            </div>
          </div>
        )}

        {/* NOTÍCIAS */}
        {activeTab === 'noticias' && canManageData && (
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex justify-between mb-4">
              <h3 className="text-xl font-bold">📰 Notícias (Últimas 4 no App)</h3>
              <button onClick={() => openModal('newNoticia')} className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg"><Newspaper size={18} />Nova</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {noticiasFiltradas.map(n => (
                <div key={n.id} className="border rounded-lg p-4">
                  {n.imagem && <img src={n.imagem} alt={n.titulo} className="w-full h-48 object-cover rounded-lg mb-3" />}
                  <h4 className="font-bold text-lg">{n.titulo}</h4>
                  <p className="text-sm text-gray-600 mt-2">{n.conteudo?.substring(0, 100)}...</p>
                  <div className="flex justify-between items-center mt-3">
                    <span className={`px-2 py-1 text-xs rounded-full ${n.publicado ? 'bg-green-100 text-green-800' : 'bg-gray-100'}`}>{n.publicado ? 'Publicado' : 'Rascunho'}</span>
                    <div className="flex gap-2">
                      <button onClick={() => openModal('editNoticia', n)} className="text-blue-600 p-2"><Edit size={18} /></button>
                      <button onClick={() => deletar('noticias', n.id)} className="text-red-600 p-2"><Trash2 size={18} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* AGENDA */}
        {activeTab === 'agenda' && canManageData && (
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex justify-between mb-4">
              <h3 className="text-xl font-bold">📅 Agenda (Últimos 4 no App)</h3>
              <button onClick={() => openModal('newEvento')} className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg"><CalendarDays size={18} />Novo</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {agendaFiltrada.map(e => (
                <div key={e.id} className="border rounded-lg p-4">
                  {e.imagem && <img src={e.imagem} alt={e.titulo} className="w-full h-48 object-cover rounded-lg mb-3" />}
                  <h4 className="font-bold text-lg">{e.titulo}</h4>
                  <p className="text-sm text-gray-600">📍 {e.local}</p>
                  <p className="text-sm text-blue-600">📅 {new Date(e.data).toLocaleDateString('pt-BR')}</p>
                  <div className="flex justify-between items-center mt-3">
                    <span className={`px-2 py-1 text-xs rounded-full ${e.publicado ? 'bg-green-100 text-green-800' : 'bg-gray-100'}`}>{e.publicado ? 'Publicado' : 'Rascunho'}</span>
                    <div className="flex gap-2">
                      <button onClick={() => openModal('editEvento', e)} className="text-blue-600 p-2"><Edit size={18} /></button>
                      <button onClick={() => deletar('agenda', e.id)} className="text-red-600 p-2"><Trash2 size={18} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* DEMANDAS */}
        {activeTab === 'demandas' && canManageData && (
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex justify-between mb-4">
              <h3 className="text-xl font-bold">📋 Demandas ({demandasFiltradas.length})</h3>
              <button onClick={() => openModal('newDemanda')} className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg"><FileText size={18} />Nova</button>
            </div>
            <div className="grid grid-cols-1 gap-4">
              {demandasFiltradas.map(d => (
                <div key={d.id} className="border rounded-lg p-4">
                  <div className="flex gap-4">
                    {d.imagem && (
                      <img src={d.imagem} alt={d.categoria} className="w-32 h-32 object-cover rounded-lg" />
                    )}
                    <div className="flex-1">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">{d.categoria}</span>
                          <span className={`ml-2 px-3 py-1 text-sm rounded-full ${d.estado === 'Completo' ? 'bg-green-100 text-green-800' : d.estado === 'Pendente' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>{d.estado}</span>
                        </div>
                        <button onClick={() => openModal('editDemanda', d)} className="text-blue-600 hover:underline flex items-center gap-1"><Edit size={16} />Editar</button>
                      </div>
                      <p className="text-gray-700 mb-2"><strong>Cidadão:</strong> {d.Nome}</p>
                      <p className="text-gray-600 text-sm mb-2">{d.descricao}</p>
                      <p className="text-gray-500 text-sm">📍 {d.endereco}</p>
                      {d.setorEncaminhado && <p className="text-gray-500 text-sm mt-1">🏢 {d.setorEncaminhado}</p>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CONSULTAS/EXAMES */}
        {activeTab === 'consultas' && canManageData && (
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex justify-between mb-4">
              <h3 className="text-xl font-bold">🏥 Consultas/Exames ({consultasFiltradas.length})</h3>
              <button onClick={() => openModal('newConsulta')} className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg"><FileText size={18} />Nova</button>
            </div>
            <div className="grid grid-cols-1 gap-4">
              {consultasFiltradas.map(c => (
                <div key={c.id} className="border rounded-lg p-4">
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs text-gray-500">Paciente</p>
                      <p className="font-semibold">{c.NomeP}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Tipo</p>
                      <p className="font-semibold">{c.TipoDeConsulta}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Status</p>
                      <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">{c.statusConsulta || 'Agendado'}</span>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">TFD</p>
                      <p className="font-semibold">{c.precisaTransporte ? '✅ Sim' : '❌ Não'}</p>
                    </div>
                  </div>
                  {c.documentos && c.documentos.length > 0 && (
                    <div className="mt-3 pt-3 border-t">
                      <p className="text-xs text-gray-500 mb-2">Documentos ({c.documentos.length})</p>
                      <div className="flex flex-wrap gap-2">
                        {c.documentos.map((doc, idx) => (
                          <a key={idx} href={doc.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-600 rounded-lg text-xs hover:bg-blue-100">
                            <Paperclip size={12} />{doc.nome}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => openModal('viewConsulta', c)} className="flex items-center gap-1 text-blue-600 hover:underline text-sm"><Eye size={14} />Ver Detalhes</button>
                    <button onClick={() => openModal('editConsulta', c)} className="flex items-center gap-1 text-green-600 hover:underline text-sm"><Edit size={14} />Editar</button>
                    <button onClick={() => deletar('consultas', c.id)} className="flex items-center gap-1 text-red-600 hover:underline text-sm"><Trash2 size={14} />Deletar</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* USUÁRIOS */}
        {activeTab === 'usuarios' && canManageUsers && (
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex justify-between mb-4">
              <h3 className="text-xl font-bold">👥 Usuários ({usuarios.length})</h3>
              <button onClick={() => openModal('newUsuario')} className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg"><UserPlus size={18} />Novo</button>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left">Nome</th>
                  <th className="px-4 py-3 text-left">CPF</th>
                  <th className="px-4 py-3 text-left">Tipo</th>
                  <th className="px-4 py-3 text-left">Ações</th>
                </tr>
              </thead>
              <tbody>
                {usuarios.map(u => (
                  <tr key={u.id} className="border-t">
                    <td className="px-4 py-3">{u.nome}</td>
                    <td className="px-4 py-3">{u.cpf}</td>
                    <td className="px-4 py-3">
                      <span className={u.tipo === 'admin' ? 'px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold' : 'px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs'}>
                        {u.tipo === 'admin' ? '👑 Admin' : '👤 Servidor'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => openModal('editUsuario', u)} className="text-blue-600 mr-3"><Edit size={14} /></button>
                      <button onClick={() => deletar('usuarios', u.id)} className="text-red-600"><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* MODAL */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
            <div className="bg-white rounded-xl p-6 w-full max-w-4xl my-8">
              <div className="flex justify-between mb-6">
                <h3 className="text-xl font-bold">
                  {modalType === 'viewConsulta' ? '🔍 Detalhes da Consulta' : modalType.replace('new', 'Nova ').replace('edit', 'Editar ')}
                </h3>
                <button onClick={() => setShowModal(false)}><X size={24} /></button>
              </div>
              
              <div className="space-y-4 max-h-[70vh] overflow-y-auto">
                {/* NOTÍCIAS */}
                {(modalType.includes('Noticia')) && (
                  <>
                    <input type="text" value={formData.titulo || ''} onChange={(e) => setFormData({...formData, titulo: e.target.value})} placeholder="Título" className="w-full px-4 py-2 border rounded-lg" />
                    <input type="text" value={formData.link || ''} onChange={(e) => setFormData({...formData, link: e.target.value})} placeholder="Link" className="w-full px-4 py-2 border rounded-lg" />
                    <textarea value={formData.conteudo || ''} onChange={(e) => setFormData({...formData, conteudo: e.target.value})} rows={6} placeholder="Conteúdo" className="w-full px-4 py-2 border rounded-lg" />
                    <div className="border-2 border-dashed rounded-lg p-4">
                      <label className="flex flex-col items-center cursor-pointer">
                        <ImageIcon size={40} className="text-gray-400 mb-2" />
                        <span className="text-sm text-gray-600">Clique para adicionar imagem (foto/flyer)</span>
                        <input type="file" accept="image/*" onChange={(e) => setSelectedImage(e.target.files[0])} className="hidden" />
                      </label>
                      {selectedImage && <p className="text-sm text-green-600 mt-2">✅ {selectedImage.name}</p>}
                      {formData.imagem && !selectedImage && <img src={formData.imagem} className="mt-2 h-32 object-cover rounded" alt="Preview" />}
                    </div>
                    <label className="flex items-center"><input type="checkbox" checked={formData.publicado || false} onChange={(e) => setFormData({...formData, publicado: e.target.checked})} className="w-4 h-4 mr-2" />Publicar (exibir no app)</label>
                  </>
                )}

                {/* EVENTOS */}
                {(modalType.includes('Evento')) && (
                  <>
                    <input type="text" value={formData.titulo || ''} onChange={(e) => setFormData({...formData, titulo: e.target.value})} placeholder="Título" className="w-full px-4 py-2 border rounded-lg" />
                    <input type="date" value={formData.data || ''} onChange={(e) => setFormData({...formData, data: e.target.value})} className="w-full px-4 py-2 border rounded-lg" />
                    <input type="text" value={formData.local || ''} onChange={(e) => setFormData({...formData, local: e.target.value})} placeholder="Local" className="w-full px-4 py-2 border rounded-lg" />
                    <input type="text" value={formData.link || ''} onChange={(e) => setFormData({...formData, link: e.target.value})} placeholder="Link (opcional)" className="w-full px-4 py-2 border rounded-lg" />
                    <div className="border-2 border-dashed rounded-lg p-4">
                      <label className="flex flex-col items-center cursor-pointer">
                        <ImageIcon size={40} className="text-gray-400 mb-2" />
                        <span className="text-sm text-gray-600">Clique para adicionar imagem (foto/flyer)</span>
                        <input type="file" accept="image/*" onChange={(e) => setSelectedImage(e.target.files[0])} className="hidden" />
                      </label>
                      {selectedImage && <p className="text-sm text-green-600 mt-2">✅ {selectedImage.name}</p>}
                      {formData.imagem && !selectedImage && <img src={formData.imagem} className="mt-2 h-32 object-cover rounded" alt="Preview" />}
                    </div>
                    <label className="flex items-center"><input type="checkbox" checked={formData.publicado || false} onChange={(e) => setFormData({...formData, publicado: e.target.checked})} className="w-4 h-4 mr-2" />Publicar (exibir no app)</label>
                  </>
                )}

                {/* DEMANDAS */}
                {(modalType.includes('Demanda')) && (
                  <>
                    <select value={formData.categoria || ''} onChange={(e) => setFormData({...formData, categoria: e.target.value})} className="w-full px-4 py-2 border rounded-lg"><option value="">Selecione a Categoria</option><option value="Lixo em Terreno Baldio">Lixo em Terreno Baldio</option><option value="Buraco na Rua">Buraco na Rua</option><option value="Iluminação Pública">Iluminação Pública</option><option value="Calçada Quebrada">Calçada Quebrada</option><option value="Entulho e Caçamba">Entulho e Caçamba</option><option value="Poda de Árvore">Poda de Árvore</option><option value="Outros">Outros</option></select>
                    <textarea value={formData.descricao || ''} onChange={(e) => setFormData({...formData, descricao: e.target.value})} rows={4} placeholder="Descrição da demanda" className="w-full px-4 py-2 border rounded-lg" />
                    <input type="text" value={formData.endereco || ''} onChange={(e) => setFormData({...formData, endereco: e.target.value})} placeholder="Endereço" className="w-full px-4 py-2 border rounded-lg" />
                    
                    <div className="border-2 border-dashed rounded-lg p-4">
                      <label className="flex flex-col items-center cursor-pointer">
                        <ImageIcon size={40} className="text-gray-400 mb-2" />
                        <span className="text-sm text-gray-600">Clique para adicionar foto da demanda</span>
                        <input type="file" accept="image/*" onChange={(e) => setSelectedImage(e.target.files[0])} className="hidden" />
                      </label>
                      {selectedImage && <p className="text-sm text-green-600 mt-2">✅ {selectedImage.name}</p>}
                      {formData.imagem && !selectedImage && <img src={formData.imagem} className="mt-2 h-32 object-cover rounded" alt="Foto da demanda" />}
                    </div>

                    <select value={formData.estado || 'Pendente'} onChange={(e) => setFormData({...formData, estado: e.target.value})} className="w-full px-4 py-2 border rounded-lg"><option value="Pendente">Pendente</option><option value="Em Análise">Em Análise</option><option value="Encaminhado">Encaminhado</option><option value="Completo">Completo</option><option value="Cancelado">Cancelado</option><option value="Negado">Negado</option></select>
                    <input type="text" value={formData.setorEncaminhado || ''} onChange={(e) => setFormData({...formData, setorEncaminhado: e.target.value})} placeholder="Setor Responsável" className="w-full px-4 py-2 border rounded-lg" />
                    <input type="email" value={formData.emailSetor || ''} onChange={(e) => setFormData({...formData, emailSetor: e.target.value})} placeholder="Email do Setor" className="w-full px-4 py-2 border rounded-lg" />
                  </>
                )}

                {/* CONSULTAS - VISUALIZAÇÃO */}
                {modalType === 'viewConsulta' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-bold mb-3">👤 Dados do Paciente</h4>
                        <div className="space-y-2 text-sm">
                          <p><span className="text-gray-600">Nome:</span> <strong>{formData.NomeP}</strong></p>
                          <p><span className="text-gray-600">Email:</span> {formData.email || '-'}</p>
                          <p><span className="text-gray-600">Celular:</span> {formData.CelP || '-'}</p>
                        </div>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-bold mb-3">🏥 Dados da Consulta</h4>
                        <div className="space-y-2 text-sm">
                          <p><span className="text-gray-600">Tipo:</span> <strong>{formData.TipoDeConsulta}</strong></p>
                          <p><span className="text-gray-600">Status:</span> <span className="px-2 py-1 rounded-full bg-green-100 text-green-800">{formData.statusConsulta}</span></p>
                          <p><span className="text-gray-600">Horário:</span> {formData.horaP || '-'}</p>
                          <p><span className="text-gray-600">Local:</span> {formData.enderecoConsulta || '-'}</p>
                        </div>
                      </div>
                    </div>

                    {formData.precisaTransporte && (
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <h4 className="font-bold mb-2 flex items-center gap-2">🚗 TFD - Tratamento Fora Domicílio</h4>
                        <p className="text-sm"><span className="text-gray-600">Endereço de Coleta:</span> <strong>{formData.enderecoColeta || 'Não informado'}</strong></p>
                      </div>
                    )}

                    {formData.documentos && formData.documentos.length > 0 && (
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-bold mb-3">📎 Documentos Anexados ({formData.documentos.length})</h4>
                        <div className="space-y-2">
                          {formData.documentos.map((doc, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 bg-white rounded border">
                              <div className="flex items-center gap-2">
                                <Paperclip size={16} className="text-gray-400" />
                                <span className="text-sm">{doc.nome}</span>
                              </div>
                              <div className="flex gap-2">
                                <a href={doc.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm flex items-center gap-1">
                                  <Download size={14} />Download
                                </a>
                                <button onClick={() => deletarDocumento(formData, idx)} className="text-red-600 hover:underline text-sm flex items-center gap-1">
                                  <Trash2 size={14} />Deletar
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* CONSULTAS - EDIÇÃO */}
                {(modalType === 'newConsulta' || modalType === 'editConsulta') && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <input type="text" value={formData.NomeP || ''} onChange={(e) => setFormData({...formData, NomeP: e.target.value})} placeholder="Nome do Paciente" className="px-4 py-2 border rounded-lg" />
                      <input type="email" value={formData.email || ''} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="Email" className="px-4 py-2 border rounded-lg" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <input type="text" value={formData.CelP || ''} onChange={(e) => setFormData({...formData, CelP: e.target.value})} placeholder="Celular" className="px-4 py-2 border rounded-lg" />
                      <input type="text" value={formData.TipoDeConsulta || ''} onChange={(e) => setFormData({...formData, TipoDeConsulta: e.target.value})} placeholder="Tipo de Consulta" className="px-4 py-2 border rounded-lg" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <select value={formData.statusConsulta || 'Agendado'} onChange={(e) => setFormData({...formData, statusConsulta: e.target.value})} className="px-4 py-2 border rounded-lg"><option value="Agendado">Agendado</option><option value="Confirmado">Confirmado</option><option value="Confirmar TFD">Confirmar TFD</option><option value="Realizado">Realizado</option><option value="Cancelado">Cancelado</option></select>
                      <input type="time" value={formData.horaP || ''} onChange={(e) => setFormData({...formData, horaP: e.target.value})} className="px-4 py-2 border rounded-lg" />
                    </div>
                    <input type="text" value={formData.enderecoConsulta || ''} onChange={(e) => setFormData({...formData, enderecoConsulta: e.target.value})} placeholder="Endereço da Consulta" className="w-full px-4 py-2 border rounded-lg" />
                    
                    <div className="border-t pt-4">
                      <label className="flex items-center mb-4">
                        <input type="checkbox" checked={formData.precisaTransporte || false} onChange={(e) => setFormData({...formData, precisaTransporte: e.target.checked})} className="w-4 h-4 mr-2" />
                        <span className="font-semibold">Precisa de TFD (Tratamento Fora Domicílio)</span>
                      </label>
                      {formData.precisaTransporte && (
                        <input type="text" value={formData.enderecoColeta || ''} onChange={(e) => setFormData({...formData, enderecoColeta: e.target.value})} placeholder="Endereço de Coleta para TFD" className="w-full px-4 py-2 border rounded-lg" />
                      )}
                    </div>

                    <div className="border-t pt-4">
                      <h4 className="font-bold mb-3">📎 Documentos do Paciente</h4>
                      <div className="border-2 border-dashed rounded-lg p-4">
                        <label className="flex flex-col items-center cursor-pointer">
                          <Upload size={40} className="text-gray-400 mb-2" />
                          <span className="text-sm text-gray-600">Clique para adicionar documentos (múltiplos arquivos)</span>
                          <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={(e) => setSelectedDocuments(Array.from(e.target.files))} className="hidden" />
                        </label>
                        {selectedDocuments.length > 0 && (
                          <div className="mt-2">
                            <p className="text-sm font-semibold mb-1">Arquivos selecionados:</p>
                            {selectedDocuments.map((file, idx) => (
                              <p key={idx} className="text-xs text-green-600">✅ {file.name}</p>
                            ))}
                          </div>
                        )}
                      </div>
                      {formData.documentos && formData.documentos.length > 0 && (
                        <div className="mt-3">
                          <p className="text-sm font-semibold mb-2">Documentos já anexados:</p>
                          <div className="space-y-1">
                            {formData.documentos.map((doc, idx) => (
                              <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                                <span>{doc.nome}</span>
                                <div className="flex gap-2">
                                  <a href={doc.url} target="_blank" rel="noopener noreferrer" className="text-blue-600"><Download size={14} /></a>
                                  <button onClick={() => deletarDocumento(formData, idx)} className="text-red-600"><Trash2 size={14} /></button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* USUÁRIOS */}
                {(modalType.includes('Usuario')) && (
                  <>
                    <input type="text" value={formData.nome || ''} onChange={(e) => setFormData({...formData, nome: e.target.value})} placeholder="Nome Completo" className="w-full px-4 py-2 border rounded-lg" />
                    <input type="text" value={formData.cpf || ''} onChange={(e) => setFormData({...formData, cpf: e.target.value.replace(/\D/g, '')})} placeholder="CPF (apenas números)" maxLength={11} className="w-full px-4 py-2 border rounded-lg" />
                    <select value={formData.tipo || 'servidor'} onChange={(e) => setFormData({...formData, tipo: e.target.value})} className="w-full px-4 py-2 border rounded-lg">
                      <option value="servidor">👤 Servidor (sem acesso a Dashboard)</option>
                      <option value="admin">👑 Administrador (acesso total)</option>
                    </select>
                    <p className="text-sm text-gray-600">⚠️ O usuário fará login usando este CPF</p>
                  </>
                )}
              </div>

              {modalType !== 'viewConsulta' && (
                <button onClick={handleSave} disabled={loading} className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold mt-6 hover:bg-blue-700 disabled:bg-gray-400">
                  {loading ? '⏳ Salvando...' : '💾 Salvar'}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* RODAPÉ */}
      <footer className="bg-white border-t mt-12 py-6">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-center md:text-left">
              <p className="text-sm text-gray-600 font-semibold">© 2025 AI Tecno Gestão Pública Limitada LTDA</p>
              <p className="text-xs text-gray-500 mt-1">Todos os direitos reservados. Proibido copiar o sistema e a metodologia.</p>
            </div>
            <div className="flex items-center gap-3">
              <img src="/logo-aitecno.png" alt="AI Tecno Gestão Pública" className="h-12 opacity-80" />
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Dashboard;
